/** Timeline: a feed of events, each an icon tile on a dashed connector with a title, a time and a description. */
import type { ComponentProps, ReactNode } from "react";
import { cx } from "./cx";
import { stagger } from "./stagger";

/** The scrolling list. Give it a `key` that changes with the feed so the rows enter again. */
export function Timeline({ className, ...props }: ComponentProps<"ol">) {
  return <ol className={cx("flex min-h-0 w-full flex-1 flex-col gap-5 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden", className)} {...props} />;
}

interface TimelineItemProps extends Omit<ComponentProps<"li">, "title"> {
  /** The icon inside the tile. */
  icon: ReactNode;
  title: ReactNode;
  time: ReactNode;
  /** No connector below the last row. */
  last?: boolean;
  /** Position in the list; rows enter 45 ms apart. */
  index?: number;
  /** Let the description wrap onto a second line instead of truncating. */
  wrap?: boolean;
}

export function TimelineItem({ icon, title, time, last, index = 0, wrap, className, children, style, ...props }: TimelineItemProps) {
  return (
    <li className={cx("group/item relative flex w-full animate-rise items-start gap-3", className)} style={{ animationDelay: stagger(index, 45), ...style }} {...props}>
      {/* Hover stays inside the tile (its edge and fill): a lift or a shadow is cut off by a list that scrolls, and a scaled icon blurs (#703). */}
      <span className="relative z-10 flex size-9 shrink-0 items-center justify-center rounded-lg border-hair border-input bg-card transition-[background-color,border-color] duration-150 ease-out group-hover/item:border-border-hover group-hover/item:bg-card-hover [&>img]:size-[18px]!">
        {icon}
      </span>
      {!last && <span aria-hidden className="dash-y absolute bottom-[-20px] left-[17.5px] top-9 w-px" />}
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-1 pt-px">
        <div className="flex w-full items-baseline justify-between gap-2">
          <p className="truncate text-body font-medium text-foreground transition-colors">{title}</p>
          <time className="shrink-0 whitespace-nowrap text-caption text-muted-foreground tabular">{time}</time>
        </div>
        <p className={cx("text-meta text-muted-foreground", !wrap && "truncate")}>{children}</p>
      </div>
    </li>
  );
}
